// @erp/ui/brand — the marks this product is signed with, inlined so the chrome
// never waits on an asset fetch. `size` is the rendered square in px.
//
// The GEOMETRY lives in ./marks.ts, which is JSX-free on purpose: the label and
// card builders in `modules/lib` paint these same paths into printable strings
// under a tsconfig with no DOM, and one definition must serve both lanes. This
// file is only the Solid wrapper around it.
//
// A mark's colour is a FIXED ASSET COLOUR, never a theme token (theme-usage
// rule: an external brand mark must not follow the active theme). For Shippra
// `color` selects between the official variants in
// ../shippra-planning/logos/Shippra/SVG: green #8cc63f (default) and baby-pink
// #ff7bac (logofavicon-babypink.svg, worn by the staff desk).
import { OUTMAN_MARK, SHIPPRA_MARK } from "./marks.js";

export const BRAND_GREEN = "#8cc63f";
export const BRAND_PINK = "#ff7bac";

/**
 * THE MODULE BAND's ground — the treatment Adobe gives the strip above their
 * header: a dark hue falling to black at both ends, with white labels over it.
 *
 * SPECTRUM'S DEFAULT BLUE (the owner, 2026-10-02 — red first, then this), and
 * built from stops our token set already carries rather than eyedropped from
 * anyone's wordmark: blue-1000 (#0054b6, `--ds-primary-hover`) in the middle,
 * blue-1100 (#004491, `--ds-primary-down`) at the shoulders, falling to
 * near-black at both ends. It is the accent family the whole product is built
 * on, two stops down.
 *
 * White reads on both: measured 7.13:1 on blue-1000 and 9.39:1 on blue-1100
 * (base.css records the same figures), so the bold white labels clear AA on
 * every part of the sweep, including the dark shoulders.
 *
 * A fixed ASSET value like the marks above, not a theme token — the band is the
 * same in light and dark, exactly as Adobe's is. Swap the two middle stops to
 * re-brand it (the red it replaced was red-1000 #b40000 / red-1100 #930000);
 * nothing else reads this.
 */
export const BRAND_BAND =
  "linear-gradient(90deg, #0a0a0a 0%, #004491 24%, #0054b6 50%, #004491 76%, #0a0a0a 100%)";
/** Outman's red, sampled from the deck's own vector layer: rgb(255, 16, 23). */
export const OUTMAN_RED = "#ff1017";

export function BrandMark(props: { size?: number; class?: string; color?: string }) {
  const size = () => props.size ?? 16;
  return (
    <svg
      viewBox={SHIPPRA_MARK.viewBox}
      width={size()}
      height={size()}
      class={props.class}
      role="img"
      aria-label="Shippra"
    >
      <path d={SHIPPRA_MARK.d} fill={props.color ?? BRAND_GREEN} />
    </svg>
  );
}

/**
 * The Outman mark — the partner that provides scanning. It appears wherever the
 * ERP hands scanning off (the printed labels' code band, the scanner chrome),
 * never as a second product logo in the app header.
 */
export function OutmanMark(props: { size?: number; class?: string; color?: string }) {
  const size = () => props.size ?? 16;
  return (
    <svg
      viewBox={OUTMAN_MARK.viewBox}
      width={size()}
      height={size()}
      class={props.class}
      role="img"
      aria-label="Outman"
    >
      <path d={OUTMAN_MARK.d} fill={props.color ?? OUTMAN_RED} fill-rule="nonzero" />
    </svg>
  );
}

// Re-exported so `@erp/ui/brand` stays the one import for chrome code; the
// strings themselves live beside the geometry, where the print lane can read
// them too.
export { BRAND_NAME, OUTMAN_DOMAIN, OUTMAN_NAME, OUTMAN_SCAN_CREDIT } from "./marks.js";
export type { BrandPath } from "./marks.js";
